import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight, Building2, CalendarDays, Camera, CheckCircle2, Handshake, Loader2, MapPin, Plane, Sparkles, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import TourRadarMap from "@/components/TourRadarMap";
import TourDateVote from "@/components/TourDateVote";
import { tourCityHref } from "@/lib/tourLinks";
import { PRIMARY_TOUR, UNLOCK_TOUR } from "@/data/tourSchedule";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// Keep monthly anchors separate from conditional sell-out candidates.
const CITIES = PRIMARY_TOUR;
const ALL_CITIES = [...PRIMARY_TOUR, ...UNLOCK_TOUR];

const PARTNER_TYPES = [
  "Presenting / Cash Sponsor",
  "Pole Studio / Venue",
  "Hotel / Travel",
  "Food / Catering",
  "Alcohol / Beverage",
  "Lingerie / Intimates",
  "Wigs / Hair / Beauty",
  "Sexual Wellness / Adult Lifestyle",
  "Polewear / Heels / Fashion",
  "Photography / Print",
  "Wellness / Recovery",
  "Nightlife / Afterparty",
  "Media / Creator",
  "Transportation",
  "Other",
];

const FUNDING_GATES = [
  "Venue + local instructor",
  "Travel + hotel",
  "Food + beverage",
  "Production + film/printing",
  "Ground transportation",
  "Contingency",
];

export default function Tour() {
  const cityTrack = useRef(null);

  // Keep older links shared before city pages existed working.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const requested = params.get("city")?.toLowerCase();
    if (requested !== "houston" && requested !== "chicago") return;
    params.delete("city");
    const oldAnchor = window.location.hash.toLowerCase();
    const intent = oldAnchor.includes("partner") ? "#partner" : oldAnchor.includes("book") ? "#book" : "#attend";
    window.location.replace("/tour/" + requested + (params.toString() ? "?" + params.toString() : "") + intent);
  }, []);
  const initialCity = typeof window !== "undefined"
    ? new URLSearchParams(window.location.search).get("city")
    : null;
  const initialSelectedCity = ALL_CITIES.some((c) => c.city === initialCity) ? initialCity : "Houston";
  const [selectedCity, setSelectedCity] = useState(initialSelectedCity);
  const emptyInterest = { name: "", email: "", instagram: "", phone: "", interest_type: "Attend", sms_consent: false, instagram_dm_consent: false };
  const [interest, setInterest] = useState(emptyInterest);
  const [showMore, setShowMore] = useState(false);
  const [partner, setPartner] = useState({ company: "", contact_name: "", email: "", instagram_or_site: "", partner_type: "", contribution: "", desired_activation: "" });
  const [booking, setBooking] = useState({ name: "", email: "", company: "", city: initialSelectedCity, project: "" });
  const [loading, setLoading] = useState("");

  const selected = useMemo(() => ALL_CITIES.find((c) => c.city === selectedCity) || CITIES[0], [selectedCity]);

  function selectCity(city, targetId = "city-interest") {
    setSelectedCity(city);
    setBooking((b) => ({ ...b, city }));
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.set("city", city);
      window.history.replaceState({}, "", url);
      requestAnimationFrame(() => document.getElementById(targetId)?.scrollIntoView({ behavior: "smooth", block: "center" }));
    }
  }

  async function joinCity(e) {
    e.preventDefault();
    if (!interest.email.trim()) return toast.error("Enter your email to join.");
    if (interest.sms_consent && !interest.phone.trim()) return toast.error("Add a phone number to opt into texts.");
    if (interest.instagram_dm_consent && !interest.instagram.trim()) return toast.error("Add your Instagram handle to request DMs.");

    const query = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();
    const attr = (key) => (query.get(key) || "").slice(0, 120);
    setLoading("interest");
    try {
      await api.post("/tour/interest", {
        city: selected.city,
        ...interest,
        email_opt_in: true, // User intentionally joins this city's email updates.
        ref: attr("ref") || `tour:${selected.city.toLowerCase().replaceAll(" ", "-")}`,
        utm_source: attr("utm_source"),
        utm_medium: attr("utm_medium"),
        utm_campaign: attr("utm_campaign"),
        utm_content: attr("utm_content"),
        landing_path: "/tour",
      });
      toast.success(`You're on the ${selected.city} interest list. Look out for tour updates by email.`);
      setInterest(emptyInterest);
      setShowMore(false);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "We couldn't save your signup. Please try again.");
    } finally {
      setLoading("");
    }
  }

  function leadTracking() {
    const query = new URLSearchParams(window.location.search);
    const attr = (key) => (query.get(key) || "").slice(0, 120);
    return {
      ref: attr("ref") || "tour:" + selectedCity.toLowerCase().replaceAll(" ", "-"),
      utm_source: attr("utm_source"),
      utm_medium: attr("utm_medium"),
      utm_campaign: attr("utm_campaign"),
      utm_content: attr("utm_content"),
      landing_path: "/tour"
    };
  }

  async function submitPartner(e) {
    e.preventDefault();
    if (!partner.company || !partner.contact_name || !partner.email || !partner.partner_type || !partner.contribution) {
      return toast.error("Complete company, contact, email, partner type and contribution.");
    }
    setLoading("partner");
    try {
      await api.post("/tour/leads", {
        lead_type: partner.partner_type === "Pole Studio / Venue" ? "studio" : "sponsor",
        city: selected.city,
        company: partner.company,
        contact_name: partner.contact_name,
        email: partner.email,
        instagram_or_site: partner.instagram_or_site,
        category: partner.partner_type,
        contribution: partner.contribution,
        desired_activation: partner.desired_activation,
        ...leadTracking(),
      });
      toast.success("Partnership inquiry received.");
      setPartner({ company: "", contact_name: "", email: "", instagram_or_site: "", partner_type: "", contribution: "", desired_activation: "" });
    } catch {
      toast.error("Could not submit the partnership inquiry.");
    } finally {
      setLoading("");
    }
  }

  async function submitBooking(e) {
    e.preventDefault();
    if (!booking.name || !booking.email || !booking.project) return toast.error("Add your name, email and project.");
    setLoading("booking");
    try {
      await api.post("/tour/leads", {
        lead_type: "city_booking",
        city: booking.city,
        company: booking.company || booking.name,
        contact_name: booking.name,
        email: booking.email,
        instagram_or_site: "",
        category: "Commercial creative booking",
        contribution: "Paid creative / photography / content inquiry",
        desired_activation: booking.project,
        ...leadTracking(),
      });
      toast.success("Your city booking inquiry is in.");
      setBooking({ name: "", email: "", company: "", city: selected.city, project: "" });
    } catch {
      toast.error("Could not submit the booking inquiry.");
    } finally {
      setLoading("");
    }
  }

  return (
    <main className="min-h-screen bg-[#090909] text-[#F5F0E8]">
      <Seo title="Pole & Polaroids Tour 2027 | Fotos on the Geaux" description="Follow the 2027 Pole & Polaroids tour, join a city interest list, or inquire about venue, sponsor and brand partnerships." path="/tour" imageAlt="Pole & Polaroids tour by Fotos on the Geaux" />
      <nav className="sticky top-0 z-50 max-w-full overflow-x-clip border-b border-white/10 bg-[#090909]/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4">
          <a href="/" className="font-accent text-lg tracking-[0.2em]">P&amp;P<span className="text-[#E32636]">.</span></a>
          <div className="flex items-center gap-3">
            <a href="#cities" className="hidden text-xs tracking-[0.2em] text-white/60 md:block">CITIES</a>
            <a href="#vote" className="hidden text-xs tracking-[.16em] text-[#FF7C86] hover:text-white sm:block">VOTE DATES</a>
            <a href="/fun" className="rounded-full border border-white/15 px-4 py-2 text-xs tracking-[0.18em] hover:border-[#39E75F] hover:text-[#39E75F]">FUND TOUR</a>
            <a href="#partners" className="hidden rounded-full border border-white/15 px-4 py-2 text-xs tracking-[0.18em] hover:border-[#39E75F] hover:text-[#39E75F] sm:inline-flex">PARTNER</a>
          </div>
        </div>
      </nav>

      <section className="relative overflow-hidden border-b border-white/10 px-4 py-20 sm:px-5 sm:py-24 md:py-36">
        <div className="absolute inset-0 opacity-50" style={{ background: "radial-gradient(50% 70% at 15% 30%, rgba(227,38,54,.24), transparent), radial-gradient(48% 60% at 85% 25%, rgba(23,92,255,.24), transparent), radial-gradient(45% 55% at 60% 90%, rgba(138,53,242,.18), transparent)" }} />
        <div className="relative mx-auto max-w-7xl">
          <p className="font-accent text-xs tracking-[0.4em] text-[#39E75F]">FOTOS ON THE GEAUX PRESENTS · 2027</p>
          <h1 className="mt-5 w-full max-w-5xl font-display text-[clamp(3.5rem,14vw,9rem)] font-black leading-[0.86] tracking-[-.055em] md:text-[clamp(5rem,9vw,9rem)]">
            POLE &amp;<br /><span className="italic">POLAROIDS</span><br />ON TOUR.
          </h1>
          <p className="mt-8 max-w-2xl text-lg leading-relaxed text-white/72 md:text-xl">
            Movement. confidence. flash photography. physical Fotos. nightlife energy. Born in New Orleans, called north and south by Houston and Chicago—the first two cities to request the tour.
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <a href="#cities" className="inline-flex items-center gap-2 rounded-full bg-[#E32636] px-6 py-3 font-accent tracking-widest">FIND YOUR CITY <ArrowRight size={17} /></a>
            <a href="#vote" className="inline-flex items-center gap-2 rounded-full border border-white/25 px-6 py-3 font-accent tracking-widest hover:border-[#E32636]">PICK THE DATE <CalendarDays size={17} /></a>
            <a href="/fun" className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 font-accent tracking-widest hover:border-[#39E75F] hover:text-[#39E75F]"><Handshake size={17} /> FUND THE TOUR</a>
          </div>
          <div className="mt-16 grid gap-3 sm:grid-cols-3">
            {[["12", "MONTHLY ANCHORS"], ["2", "FOUNDING CITIES"], ["12", "POTENTIAL UNLOCKS"]].map(([n, l]) => (
              <div key={l} className="rounded-2xl border border-white/10 bg-white/[0.025] p-5">
                <div className="font-display text-5xl">{n}</div>
                <div className="mt-1 font-accent text-xs tracking-[0.2em] text-white/55">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section aria-label="Founding cities" className="mx-auto grid max-w-7xl gap-4 px-5 pt-12 md:grid-cols-2">
        {CITIES.filter((c) => c.active).map((c) => <a key={c.city} href={tourCityHref(c.city)} className="group flex min-h-56 flex-col justify-between rounded-[1.75rem] border border-white/20 p-6 transition hover:border-white/55 sm:p-8" style={{backgroundImage: c.city === "Houston" ? "linear-gradient(140deg,#3b1219,#101010 70%)" : "linear-gradient(140deg,#0c244a,#101010 70%)"}}>
          <span className="text-xs font-accent tracking-[.2em] text-white/70">{c.status}</span>
          <span className="font-display text-5xl font-black tracking-tight sm:text-6xl">{c.city.toUpperCase()}<span className="text-[#39E75F]">.</span></span>
          <span className="flex items-center justify-between gap-3 text-sm text-white/65">Target: {c.target} · Unconfirmed <ArrowRight className="shrink-0 transition group-hover:translate-x-1" size={20}/></span>
        </a>)}
      </section>

      <TourDateVote key={selectedCity} initialCity={selectedCity} />

      <section id="cities" className="mx-auto max-w-7xl px-5 py-24">
        <div className="max-w-3xl">
          <p className="font-accent text-xs tracking-[0.4em] text-[#175CFF]">THE ROUTE</p>
          <h2 className="mt-3 font-display text-5xl tracking-tight md:text-7xl">Demand first. Funding second. Travel third.</h2>
          <p className="mt-5 text-white/65">Houston is the Founding City of the South; Chicago is the Founding City of the North. Explore 12 monthly proposals on the map, then show support for up to 12 additional cities that may unlock after a sold-out primary show. No dates or venues are confirmed.</p>
        </div>

        <TourRadarMap cities={ALL_CITIES} getHref={tourCityHref} onFocusCity={(name) => {
          if (CITIES.some((c) => c.city === name)) {
            setSelectedCity(name);
            cityTrack.current?.querySelector('[data-tour-city="' + name + '"]')?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
          } else selectCity(name);
        }} />

        <div className="mt-10 flex items-center justify-between gap-3">
          <p className="font-accent text-xs tracking-[.2em] text-white/45">SWIPE THE ROUTE · 12 MONTHLY CITIES</p>
          <div className="flex gap-2" aria-label="Tour city carousel controls">
            <button type="button" onClick={() => cityTrack.current?.scrollBy({ left: -380, behavior: "smooth" })} aria-label="Previous cities" className="grid h-11 w-11 place-items-center rounded-full border border-white/20 bg-white/[.03] hover:border-[#39E75F]"><ChevronLeft size={19}/></button>
            <button type="button" onClick={() => cityTrack.current?.scrollBy({ left: 380, behavior: "smooth" })} aria-label="Next cities" className="grid h-11 w-11 place-items-center rounded-full border border-white/20 bg-white/[.03] hover:border-[#39E75F]"><ChevronRight size={19}/></button>
          </div>
        </div>
        <div ref={cityTrack} role="region" aria-label="Swipeable tour city selector" tabIndex={0} className="mt-5 flex snap-x snap-mandatory gap-4 overflow-x-auto pb-6 pr-5 scroll-smooth [scrollbar-width:thin]">
          {CITIES.map((c, index) => {
            const card = <>
              <img src={c.city === "Houston" ? "/campaign-assets/dancer-green.webp" : c.city === "Chicago" ? "/campaign-assets/party-red.webp" : "/campaign-assets/party-blue.webp"} alt="" loading={index < 2 ? "eager" : "lazy"} className="absolute inset-0 h-full w-full object-cover opacity-55 transition duration-500 group-hover:scale-[1.035]"/>
              <div className="absolute inset-0 bg-gradient-to-t from-[#060606] via-[#060606]/75 to-[#090909]/10"/>
              <div className="relative z-10 flex items-start justify-between gap-3">
                <span className={`rounded-full border px-3 py-1.5 text-[10px] font-accent tracking-[.16em] ${c.active ? "border-[#39E75F]/60 bg-[#101e14] text-[#39E75F]" : "border-white/25 text-white/75"}`}>{c.status}</span>
                <span className="font-accent text-sm tracking-widest text-white/60">{String(index + 1).padStart(2, "0")}/12</span>
              </div>
              <div className="relative z-10 mt-auto">
                <p className="text-xs tracking-[.25em] text-white/55">{c.state} · 2027 TOUR</p>
                <h3 className="mt-3 font-display text-6xl leading-none tracking-[-.04em]">{c.city.toUpperCase()}<span className="text-[#E32636]">.</span></h3>
                <p className="mt-4 text-xs font-accent tracking-[.16em] text-white/60">PLANNING TARGET · UNCONFIRMED</p>
                <p className="mt-2 text-base text-white/85">{c.target}</p>
              </div>
            </>;
            const frame = "group relative flex min-h-[425px] w-[83vw] max-w-[400px] flex-none snap-start flex-col justify-between overflow-hidden rounded-[2rem] border border-white/15 bg-[#151515] p-7 text-left transition hover:border-[#39E75F]/60 sm:w-[355px]";
            return c.active ? (
              <a key={c.city} data-tour-city={c.city} href={tourCityHref(c.city)} aria-label={`Explore ${c.city} tour page and join or partner`} className={frame}>
                {card}
                <span className="relative z-10 mt-7 inline-flex items-center gap-2 self-start rounded-full bg-[#39E75F] px-5 py-3 font-accent text-xs tracking-[.15em] text-[#090909]">OPEN {c.city.toUpperCase()} PAGE <ArrowRight size={16}/></span>
              </a>
            ) : (
              <article key={c.city} data-tour-city={c.city} className={frame}>
                {card}
                <div className="relative z-10 mt-7 grid grid-cols-3 gap-2">
                  <button type="button" onClick={() => selectCity(c.city, "city-interest")} className="min-h-11 rounded-full border border-white/25 bg-black/60 px-2 text-xs font-accent tracking-wider hover:border-[#39E75F]">JOIN</button>
                  <button type="button" onClick={() => selectCity(c.city, "partners")} className="min-h-11 rounded-full border border-white/25 bg-black/60 px-2 text-xs font-accent tracking-wider hover:border-[#39E75F]">PARTNER</button>
                  <button type="button" onClick={() => selectCity(c.city, "city-booking")} className="min-h-11 rounded-full border border-white/25 bg-black/60 px-2 text-xs font-accent tracking-wider hover:border-[#39E75F]">BOOK</button>
                </div>
              </article>
            );
          })}
        </div>
        <p className="mt-2 text-xs text-white/40">Houston and Chicago have dedicated founding-city pages. All other stops remain in planning and interest collection.</p>

        <section id="unlock-cities" className="mt-14 scroll-mt-24 rounded-[2rem] border border-white/15 bg-[#121212] p-5 sm:p-8">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div><p className="font-accent text-[11px] tracking-[.25em] text-[#39E75F]">WHEN A PRIMARY CITY SELLS OUT</p><h3 className="mt-3 font-display text-4xl tracking-tight sm:text-5xl">UNLOCK THE NEXT CITY.</h3></div>
            <span className="rounded-full border border-white/20 px-4 py-2 text-xs tracking-wider text-white/60">12 CONDITIONAL MARKETS</span>
          </div>
          <p className="mt-4 max-w-3xl text-sm leading-6 text-white/65">Second cities are not on sale. A full primary show begins the conversation, but local demand, venue availability, staffing and funding must be verified before any expansion date is released.</p>
          <div className="mt-7 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {UNLOCK_TOUR.map((c) => <button key={c.city} type="button" onClick={() => selectCity(c.city)} className="flex min-h-36 flex-col items-start justify-between rounded-2xl border border-white/15 bg-black/25 p-5 text-left transition hover:border-[#39E75F]/65 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#39E75F]">
              <span className="text-[11px] font-accent tracking-wider text-[#39E75F]">{c.month.toUpperCase()} · AFTER {c.unlockOf.toUpperCase()} SELLS OUT</span>
              <span className="font-display text-3xl leading-none">{c.city.toUpperCase()}</span>
              <span className="flex w-full items-center justify-between gap-2 text-xs text-white/60">Potential {c.target} · Join interest list <ArrowRight size={15}/></span>
            </button>)}
          </div>
          <p className="mt-4 text-xs text-white/45">Potential second-city dates are nonbinding, and no date will automatically unlock on sellout.</p>
        </section>

        <div id="city-interest" className="mt-12 grid min-w-0 gap-6 rounded-3xl border border-white/10 bg-[#0d0d0d] p-5 sm:p-6 md:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] md:p-10">
          <div>
            <p className="font-accent text-xs tracking-[0.35em] text-[#39E75F]">SELECTED CITY</p>
            <h3 className="mt-2 font-display text-5xl">{selected.city}</h3>
            <div className="mt-4 flex items-center gap-2 text-white/60"><CalendarDays size={17} /> Target: {selected.target}</div>
            <div className="mt-2 text-sm text-white/40">{selected.conditional ? `Unlock candidate after ${selected.unlockOf} sells out · No tickets available` : `Alternate target: ${selected.backup}`}</div>
            <p className="mt-7 max-w-md text-white/65">Join this city's list. Demand data helps determine venue size, sponsorship and which stops move from target to confirmed. A conditional city is not a ticket or a booking.</p>
          </div>
          <form onSubmit={joinCity} className="grid gap-4 self-center" aria-label={`Join the ${selected.city} tour interest list`}>
            <div>
              <p className="font-accent text-xs tracking-[.18em] text-[#39E75F]">GET FIRST WORD WHEN WE COME THROUGH</p>
              <p className="mt-2 text-sm leading-6 text-white/60">Just your email to join. Instagram and the rest are optional, but help us connect in more ways.</p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="grid gap-1.5">
                <label htmlFor="tour-name" className="text-xs text-white/70">Name <span className="text-white/40">(optional)</span></label>
                <Input id="tour-name" name="name" autoComplete="name" maxLength={160} placeholder="What should we call you?" value={interest.name} onChange={(e) => setInterest({ ...interest, name: e.target.value })} className="min-h-12 border-white/15 bg-transparent" />
              </div>
              <div className="grid gap-1.5">
                <label htmlFor="tour-email" className="text-xs text-white/70">Email <span className="text-[#39E75F]">*</span></label>
                <Input id="tour-email" name="email" type="email" required autoComplete="email" maxLength={254} placeholder="you@example.com" value={interest.email} onChange={(e) => setInterest({ ...interest, email: e.target.value })} className="min-h-12 border-white/15 bg-transparent" />
              </div>
            </div>
            <div className="grid gap-1.5">
              <label htmlFor="tour-instagram" className="text-xs text-white/70">Instagram @handle <span className="text-white/40">(optional)</span></label>
              <Input id="tour-instagram" name="instagram" autoCapitalize="none" autoCorrect="off" spellCheck={false} maxLength={100} placeholder="@yourhandle" value={interest.instagram} onChange={(e) => setInterest({ ...interest, instagram: e.target.value, instagram_dm_consent: e.target.value.trim() ? interest.instagram_dm_consent : false })} className="min-h-12 border-white/15 bg-transparent" />
            </div>
            <button type="button" aria-expanded={showMore} aria-controls="tour-more-details" onClick={() => setShowMore(!showMore)} className="w-fit border-b border-dashed border-white/35 pb-1 text-left text-xs text-white/70 transition hover:text-white">
              {showMore ? "− Fewer details" : "+ Add phone, interests & contact preferences (optional)"}
            </button>
            {showMore && <div id="tour-more-details" className="grid gap-4 rounded-2xl border border-white/10 bg-white/[.025] p-4">
              <div className="grid gap-1.5">
                <label htmlFor="tour-phone" className="text-xs text-white/70">Mobile phone <span className="text-white/40">(optional)</span></label>
                <Input id="tour-phone" name="phone" type="tel" autoComplete="tel" maxLength={40} placeholder="(555) 555-5555" value={interest.phone} onChange={(e) => setInterest({ ...interest, phone: e.target.value, sms_consent: e.target.value ? interest.sms_consent : false })} className="min-h-12 border-white/15 bg-transparent" />
              </div>
              <div className="grid gap-1.5">
                <label className="text-xs text-white/70">What are you most interested in?</label>
                <Select value={interest.interest_type} onValueChange={(value) => setInterest({ ...interest, interest_type: value })}>
                  <SelectTrigger className="min-h-12 border-white/15 bg-transparent"><SelectValue /></SelectTrigger>
                  <SelectContent className="border-white/15 bg-[#0d0d0d] text-white">
                    {["Attend", "Perform or teach", "Create content", "Ambassador / promote", "Sponsor / partner", "Help bring it to my city"].map((value) => <SelectItem key={value} value={value}>{value}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {interest.instagram.trim() && <label className="flex items-start gap-2.5 text-xs leading-5 text-white/65">
                <input type="checkbox" className="mt-1 h-4 w-4 shrink-0 accent-[#39E75F]" checked={interest.instagram_dm_consent} onChange={(e) => setInterest({ ...interest, instagram_dm_consent: e.target.checked })} />
                You may contact my Instagram account about Pole &amp; Polaroids tour opportunities. DM delivery depends on Instagram settings.
              </label>}
              <label className="flex items-start gap-2.5 text-xs leading-5 text-white/65">
                <input type="checkbox" className="mt-1 h-4 w-4 shrink-0 accent-[#39E75F]" checked={interest.sms_consent} onChange={(e) => setInterest({ ...interest, sms_consent: e.target.checked })} />
                I agree to receive promotional text messages from Fotos on the Geaux about Pole &amp; Polaroids at the number provided. Message frequency varies; message/data rates may apply. Reply STOP to opt out or HELP for assistance. Consent is not required to purchase or join this email list.
              </label>
            </div>}
            <button type="submit" className="flex min-h-12 items-center justify-center gap-2 rounded-full bg-[#175CFF] px-6 font-accent tracking-widest transition hover:bg-[#346fff] disabled:opacity-60" disabled={loading === "interest"}>
              {loading === "interest" ? <Loader2 className="animate-spin" size={17} /> : <>JOIN {selected.city.toUpperCase()} <ArrowRight size={17} /></>}
            </button>
            <p className="text-[11px] leading-5 text-white/45">By joining, you agree to occasional email updates about your selected city's tour plans. Unsubscribe any time. We won't text or DM you based on your number or handle alone. Dates and venues are still being confirmed.</p>
          </form>
        </div>
      </section>

      <section className="border-y border-white/10 bg-[#0d0d0d] px-5 py-24">
        <div className="mx-auto max-w-7xl">
          <p className="font-accent text-xs tracking-[0.4em] text-[#E32636]">THE FUNDING GATE</p>
          <h2 className="mt-3 max-w-4xl font-display text-5xl tracking-tight md:text-7xl">No out-of-state stop travels unfunded.</h2>
          <p className="mt-5 max-w-2xl text-white/65">A city only clears for booking when contracted cash and approved in-kind support cover the operating trip.</p>
          <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {FUNDING_GATES.map((g) => <div key={g} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-black/20 p-4"><CheckCircle2 size={18} className="text-[#39E75F]" /><span>{g}</span></div>)}
          </div>
        </div>
      </section>

      <section id="partners" className="mx-auto max-w-7xl px-5 py-24">
        <div className="grid min-w-0 gap-10 lg:grid-cols-[minmax(0,.9fr)_minmax(0,1.1fr)]">
          <div>
            <p className="font-accent text-xs tracking-[0.4em] text-[#8A35F2]">TOUR PARTNERS</p>
            <h2 className="mt-3 font-display text-5xl tracking-tight md:text-7xl">Fund a city. Own an activation.</h2>
            <p className="mt-5 text-white/65">Cash, venue, hotel, food, beverage, lingerie, hair, beauty, sexual wellness, fashion, nightlife, transportation and production partners can enter the same pipeline.</p>
            <div className="mt-8 space-y-3 text-sm text-white/60">
              <div className="flex gap-3"><Building2 size={18} className="text-[#175CFF]" /> Local partners can support one city.</div>
              <div className="flex gap-3"><Plane size={18} className="text-[#39E75F]" /> Regional partners can cover a tour leg.</div>
              <div className="flex gap-3"><Sparkles size={18} className="text-[#E32636]" /> National partners can activate across the full tour.</div>
            </div>
          </div>

          <form onSubmit={submitPartner} className="grid gap-3 rounded-3xl border border-white/10 bg-[#0d0d0d] p-6 md:p-8">
            <div className="grid gap-3 sm:grid-cols-2">
              <Input placeholder="Company *" value={partner.company} onChange={(e) => setPartner({ ...partner, company: e.target.value })} className="border-white/15 bg-transparent" />
              <Input placeholder="Contact name *" value={partner.contact_name} onChange={(e) => setPartner({ ...partner, contact_name: e.target.value })} className="border-white/15 bg-transparent" />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Input type="email" placeholder="Email *" value={partner.email} onChange={(e) => setPartner({ ...partner, email: e.target.value })} className="border-white/15 bg-transparent" />
              <Input placeholder="Website / Instagram" value={partner.instagram_or_site} onChange={(e) => setPartner({ ...partner, instagram_or_site: e.target.value })} className="border-white/15 bg-transparent" />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Select value={selectedCity} onValueChange={(v) => selectCity(v, "partners")}>
                <SelectTrigger className="border-white/15 bg-transparent"><SelectValue /></SelectTrigger>
                <SelectContent className="border-white/15 bg-[#0d0d0d] text-white">{ALL_CITIES.map((c) => <SelectItem key={c.city} value={c.city}>{c.city}{c.conditional ? " · unlock candidate" : ""}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={partner.partner_type} onValueChange={(v) => setPartner({ ...partner, partner_type: v })}>
                <SelectTrigger className="border-white/15 bg-transparent"><SelectValue placeholder="Partner type *" /></SelectTrigger>
                <SelectContent className="border-white/15 bg-[#0d0d0d] text-white">{PARTNER_TYPES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Textarea placeholder="What can you contribute? Cash amount, room nights, venue hours, catering, products, transportation, etc. *" value={partner.contribution} onChange={(e) => setPartner({ ...partner, contribution: e.target.value })} className="min-h-24 border-white/15 bg-transparent" />
            <Textarea placeholder="Activation idea / what you want from the partnership" value={partner.desired_activation} onChange={(e) => setPartner({ ...partner, desired_activation: e.target.value })} className="min-h-24 border-white/15 bg-transparent" />
            <button className="flex min-h-12 items-center justify-center gap-2 rounded-full bg-[#39E75F] px-6 font-accent tracking-widest text-[#090909]" disabled={loading === "partner"}>
              {loading === "partner" ? <Loader2 className="animate-spin" size={17} /> : <>SUBMIT TOUR PARTNERSHIP <Handshake size={17} /></>}
            </button>
          </form>
        </div>
      </section>

      <section id="city-booking" className="border-t border-white/10 bg-[#0d0d0d] px-5 py-24">
        <div className="mx-auto grid max-w-7xl min-w-0 gap-10 lg:grid-cols-[minmax(0,.9fr)_minmax(0,1.1fr)]">
          <div>
            <p className="font-accent text-xs tracking-[0.4em] text-[#175CFF]">WHILE JC IS IN YOUR CITY</p>
            <h2 className="mt-3 font-display text-5xl tracking-tight md:text-7xl">Book the trip beyond the event.</h2>
            <p className="mt-5 text-white/65">The tour can anchor brand campaigns, nightlife/event coverage, creator shoots, portraits, hospitality photography and commercial content while production is already in-market.</p>
          </div>
          <form onSubmit={submitBooking} className="grid gap-3 rounded-3xl border border-white/10 bg-black/20 p-6 md:p-8">
            <div className="grid gap-3 sm:grid-cols-2">
              <Input placeholder="Your name *" value={booking.name} onChange={(e) => setBooking({ ...booking, name: e.target.value })} className="border-white/15 bg-transparent" />
              <Input type="email" placeholder="Email *" value={booking.email} onChange={(e) => setBooking({ ...booking, email: e.target.value })} className="border-white/15 bg-transparent" />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Input placeholder="Brand / company" value={booking.company} onChange={(e) => setBooking({ ...booking, company: e.target.value })} className="border-white/15 bg-transparent" />
              <Select value={booking.city} onValueChange={(v) => setBooking({ ...booking, city: v })}>
                <SelectTrigger className="border-white/15 bg-transparent"><SelectValue /></SelectTrigger>
                <SelectContent className="border-white/15 bg-[#0d0d0d] text-white">{ALL_CITIES.map((c) => <SelectItem key={c.city} value={c.city}>{c.city}{c.conditional ? " · unlock candidate" : ""}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Textarea placeholder="Tell us what you want produced while the tour is in town. *" value={booking.project} onChange={(e) => setBooking({ ...booking, project: e.target.value })} className="min-h-28 border-white/15 bg-transparent" />
            <button className="flex min-h-12 items-center justify-center gap-2 rounded-full bg-[#175CFF] px-6 font-accent tracking-widest" disabled={loading === "booking"}>
              {loading === "booking" ? <Loader2 className="animate-spin" size={17} /> : <>REQUEST A CITY BOOKING <Camera size={17} /></>}
            </button>
          </form>
        </div>
      </section>

      <footer className="border-t border-white/10 px-5 py-12">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 text-sm text-white/45 md:flex-row md:items-center md:justify-between">
          <div className="font-accent tracking-[0.18em] text-white">POLE &amp; POLAROIDS · BY FOTOS ON THE GEAUX</div>
          <div>2027 TOUR MVP · DATES SUBJECT TO VENUE + FUNDING CONFIRMATION</div>
        </div>
      </footer>
    </main>
  );
}
